import { useState } from 'react';
import { CheckCircle, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Chip, CircularProgress } from '@mui/material';

import type { SyncChangeResponse } from '@store/generated/admin';

import { useGetBundleSyncPreviewQuery } from '../store/hooks';

/** One line describing a pending sync change, e.g. 'Add "Pro Bundle" (qty 1)'. */
export function describeChange(c: SyncChangeResponse): string {
  const name = c.bundle_name || c.bundle_id;
  switch (c.action) {
    case 'created':
      return `Add “${name}” to Stripe (qty ${c.new_quantity ?? '?'})`;
    case 'removed':
      return `Remove “${name}” from Stripe`;
    case 'updated':
      return `Update “${name}” quantity ${c.old_quantity ?? '?'} → ${c.new_quantity ?? '?'}`;
    default:
      return `${c.action} “${name}”`;
  }
}

interface BundleSyncIndicatorProps {
  organizationId: string;
}

/**
 * Async indicator of whether the org's Stripe subscription matches its current
 * bundle-assignment state — i.e. the drift the "Sync" button would reconcile.
 * Backed by a read-only dry-run of the sync, it loads independently of the org
 * page so it never blocks rendering, and stays silent when there's no Stripe
 * subscription or the preview can't be computed.
 */
export function BundleSyncIndicator({ organizationId }: BundleSyncIndicatorProps) {
  const { data, isLoading, isError } = useGetBundleSyncPreviewQuery(
    { organizationId },
    { skip: !organizationId },
  );
  const [expanded, setExpanded] = useState(false);

  // No Stripe subscription / failed dry-run -> success=false; stay quiet.
  if (isError || (data && data.success === false)) return null;

  const changes = data?.changes ?? [];

  return (
    <div>
      <dt className="text-sm text-gray-500">Bundle Sync</dt>
      <dd className="text-sm font-medium">
        {isLoading ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <CircularProgress size={12} />
            <span className="text-gray-500">Checking…</span>
          </Box>
        ) : changes.length === 0 ? (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            icon={<CheckCircle />}
            label="In sync with bundle assignments"
          />
        ) : (
          <Box>
            <Chip
              size="small"
              color="warning"
              label={`Out of sync — ${changes.length} change${changes.length === 1 ? '' : 's'}`}
              onClick={() => setExpanded(e => !e)}
              onDelete={() => setExpanded(e => !e)}
              deleteIcon={expanded ? <ExpandLess /> : <ExpandMore />}
              sx={{ cursor: 'pointer' }}
            />
            {expanded && (
              <ul className="mt-1 ml-1 list-disc list-inside text-xs text-gray-700 space-y-0.5">
                {changes.map((c, i) => (
                  <li key={`${c.bundle_id}-${c.action}-${i}`}>{describeChange(c)}</li>
                ))}
              </ul>
            )}
            <div className="text-xs text-gray-400 mt-0.5">
              These are applied by the “Sync” button.
            </div>
          </Box>
        )}
      </dd>
    </div>
  );
}
